import BrowserBroadcastChannelService from './BrowserBroadcastChannelService.js'

class ChatSyncService {
  constructor() {
    this.broadcastService = BrowserBroadcastChannelService.getInstance()
    this.messageListeners = new Set()

    // Subscribe to chat-related messages
    this.broadcastService.subscribe(
      'USER_MESSAGE_SENT',
      this.handleMessageSent.bind(this)
    )
  }

  /**
   * Broadcast that a message was sent from this tab
   * @param {Object} messageData - { conversationId, message, timestamp, etc. }
   */
  broadcastMessageSent(messageData) {
    this.broadcastService.broadcast('USER_MESSAGE_SENT', {
      ...messageData,
      fromTabId: this.broadcastService.tabId
    })
  }

  /**
   * Handle incoming message from other tabs
   * @param {Object} data - Message data from broadcast
   */
  handleMessageSent(data) {
    // Only process messages from other tabs
    if (data.fromTabId !== this.broadcastService.tabId) {
      // Notify all registered listeners
      for (const listener of this.messageListeners) {
        try {
          listener({ type: 'USER_MESSAGE_SENT', data })
        } catch (error) {
          console.error('Error in message listener:', error)
        }
      }
    }
  }

  /**
   * Register a callback to be called when a message is received from another tab
   * @param {Function} callback - Function to call with message data
   * @returns {Function} - Unsubscribe function
   */
  onMessageReceived(callback) {
    this.messageListeners.add(callback)
    return () => this.messageListeners.delete(callback)
  }

  /**
   * Legacy method for backwards compatibility
   * @param {Function} callback - Function to call with message data
   * @returns {Function} - Unsubscribe function
   */
  listen(callback) {
    return this.onMessageReceived(callback)
  }

  /**
   * Broadcast typing status
   * @param {string} conversationId
   * @param {boolean} isTyping
   */
  broadcastTypingStatus(conversationId, isTyping) {
    this.broadcastService.broadcast('USER_TYPING', {
      conversationId,
      isTyping,
      fromTabId: this.broadcastService.tabId
    })
  }

  /**
   * Broadcast that user read messages in a conversation
   * @param {string} conversationId
   * @param {string} lastReadMessageId
   */
  broadcastMessagesRead(conversationId, lastReadMessageId) {
    this.broadcastService.broadcast('MESSAGES_READ', {
      conversationId,
      lastReadMessageId,
      fromTabId: this.broadcastService.tabId
    })
  }

  /**
   * Clean up resources
   */
  destroy() {
    this.messageListeners.clear()
    // Unsubscribe from broadcast service
    this.broadcastService.unsubscribe(
      'USER_MESSAGE_SENT',
      this.handleMessageSent.bind(this)
    )
  }

  /**
   * Get singleton instance
   */
  static getInstance() {
    if (!ChatSyncService.instance) {
      ChatSyncService.instance = new ChatSyncService()
    }
    return ChatSyncService.instance
  }
}

export default ChatSyncService
